/**
 * Bootstrap 5 Modal monkey-patch for Shadow DOM.
 *
 * ??  WHY THIS EXISTS:
 * Bootstrap's Modal._showElement() moves the modal element into document.body
 * so it can sit above everything else.  Inside a Shadow DOM that move rips
 * the element out of the shadow tree, breaking style encapsulation and event
 * delegation.  We override _showElement to keep the modal *in place*.
 *
 * ??  MAINTENANCE:
 * This patch targets Bootstrap 5.3.x.  After upgrading Bootstrap, compare
 * the original _showElement source and update this file if the internal API
 * has changed.  The original source lives in:
 *   node_modules/bootstrap/js/src/modal.js  ?  _showElement()
 *
 * ??  BACKDROP:
 * Bootstrap's native backdrop is appended to document.body as well.
 * We inject our own `.bs5-backdrop` element into the shadow root instead
 * (see `ensureBackdrop`).  It is shown/hidden via CSS and the modal events.
 */

import * as bootstrap5 from 'bootstrap';

const Modal = bootstrap5.Modal;

/**
 * Apply the monkey-patch on Modal.prototype._showElement.
 * Safe to call multiple times ? it only patches once.
 */
export function patchModalForShadowDOM() {
    if (Modal.__shadowPatched) return;
    Modal.__shadowPatched = true;

    Modal.prototype._showElement = function (relatedTarget) {
        // NOTE: the original `document.body.append(this._element)` is
        // intentionally removed to keep the modal inside the Shadow DOM.

        this._element.style.display = 'block';
        this._element.removeAttribute('aria-hidden');
        this._element.setAttribute('aria-modal', true);
        this._element.setAttribute('role', 'dialog');
        this._element.scrollTop = 0;

        const modalBody = this._dialog?.querySelector('.modal-body');
        if (modalBody) modalBody.scrollTop = 0;

        // Bootstrap internal reflow trigger
        // eslint-disable-next-line no-unused-expressions
        this._element.offsetHeight;

        this._element.classList.add('show');

        const transitionComplete = () => {
            if (this._config.focus) this._focustrap.activate();
            this._isTransitioning = false;
            this._element.dispatchEvent(
                new Event('shown.bs.modal', { bubbles: true })
            );
        };

        this._queueCallback(transitionComplete, this._dialog, this._isAnimated());
    };
}

/**
 * Ensure the shadow root has a backdrop element for modals.
 *
 * @param {ShadowRoot} shadowRoot
 */
export function ensureBackdrop(shadowRoot) {
    if (shadowRoot.querySelector('.bs5-backdrop')) return;

    const backdrop = document.createElement('div');
    backdrop.className = 'bs5-backdrop';
    backdrop.style.display = 'none';
    shadowRoot.appendChild(backdrop);
}